<template>
  <div class="mfa-setup-container">
    <a-steps :current="currentStep" :status="stepStatus" class="mfa-steps">
      <a-step :title="$t('mfa.chooseMethod')" />
      <a-step :title="$t('mfa.setup')" />
      <a-step :title="$t('mfa.verify')" />
      <a-step :title="$t('mfa.complete')" />
    </a-steps>

    <div class="mfa-content">
      <!-- Step 1: Choose Method -->
      <div v-if="currentStep === 0" class="method-selection">
        <div class="intro-section">
          <h3 class="section-title">{{ $t("mfa.chooseMethodTitle") }}</h3>
          <p class="section-description">
            {{ $t("mfa.chooseMethodDescription") }}
          </p>
        </div>

        <div class="method-cards">
          <!-- Authenticator App Card (Recommended) -->
          <a-card
            :class="[
              'method-card',
              'recommended-card',
              { selected: selectedMethod === 'app' },
            ]"
            :hoverable="true"
            @click="selectedMethod = 'app'"
          >
            <div class="card-header">
              <div class="method-icon-wrapper">
                <a-icon type="mobile" class="method-icon" />
                <a-badge
                  :count="$t('mfa.recommended')"
                  class="recommended-badge"
                />
              </div>
              <a-radio
                :checked="selectedMethod === 'app'"
                @click.stop="selectedMethod = 'app'"
              />
            </div>
            <div class="card-body">
              <h4 class="method-title">{{ $t("mfa.authenticatorApp") }}</h4>
              <p class="method-description">
                {{ $t("mfa.authenticatorAppDescription") }}
              </p>
              <div class="method-benefits">
                <div class="benefit-item">
                  <a-icon
                    type="check-circle"
                    theme="filled"
                    class="benefit-icon"
                  />
                  <span>{{ $t("mfa.moreSecure") }}</span>
                </div>
                <div class="benefit-item">
                  <a-icon
                    type="check-circle"
                    theme="filled"
                    class="benefit-icon"
                  />
                  <span>{{ $t("mfa.fasterLogin") }}</span>
                </div>
                <div class="benefit-item">
                  <a-icon
                    type="check-circle"
                    theme="filled"
                    class="benefit-icon"
                  />
                  <span>{{ $t("mfa.moreReliable") }}</span>
                </div>
                <div class="benefit-item">
                  <a-icon
                    type="check-circle"
                    theme="filled"
                    class="benefit-icon"
                  />
                  <span>{{ $t("mfa.noNetworkNeeded") }}</span>
                </div>
                <div class="benefit-item">
                  <a-icon
                    type="check-circle"
                    theme="filled"
                    class="benefit-icon"
                  />
                  <span>{{ $t("mfa.industryStandard") }}</span>
                </div>
              </div>
            </div>
          </a-card>

          <!-- SMS Card -->
          <a-card
            :class="[
              'method-card',
              { selected: selectedMethod === 'sms', disabled: !hasPhone },
            ]"
            :hoverable="hasPhone"
            @click="hasPhone && (selectedMethod = 'sms')"
          >
            <div class="card-header">
              <a-icon type="message" class="method-icon" />
              <a-radio
                :checked="selectedMethod === 'sms'"
                :disabled="!hasPhone"
                @click.stop="hasPhone && (selectedMethod = 'sms')"
              />
            </div>
            <div class="card-body">
              <h4 class="method-title">{{ $t("mfa.sms") }}</h4>
              <p class="method-description">{{ $t("mfa.smsDescription") }}</p>
              <div v-if="!hasPhone" class="phone-warning">
                <a-alert
                  :message="$t('mfa.phoneRequired')"
                  type="warning"
                  show-icon
                  :closable="false"
                />
              </div>
            </div>
          </a-card>
        </div>

        <div class="action-buttons">
          <a-button
            type="primary"
            size="large"
            @click="handleSetup"
            :loading="loading"
            :disabled="
              !selectedMethod || (selectedMethod === 'sms' && !hasPhone)
            "
            class="continue-button"
          >
            {{ $t("mfa.continue") }}
            <a-icon type="arrow-right" />
          </a-button>
        </div>
      </div>

      <!-- Step 2: Setup (App method - show QR code) -->
      <div
        v-if="currentStep === 1 && selectedMethod === 'app'"
        class="app-setup"
      >
        <div class="setup-header">
          <h3 class="section-title">{{ $t("mfa.setupAuthenticatorApp") }}</h3>
          <p class="section-description">
            {{ $t("mfa.setupAuthenticatorAppDescription") }}
          </p>
        </div>

        <!-- Download Instructions -->
        <a-card class="instruction-card" :bordered="false">
          <div slot="title" class="instruction-title">
            <a-icon type="download" />
            <span>{{ $t("mfa.step1Download") }}</span>
          </div>
          <div class="instruction-content">
            <p class="instruction-text">{{ $t("mfa.downloadInstruction") }}</p>
            <div class="app-links">
              <a-button
                type="link"
                size="large"
                @click="openAppStore('ios')"
                class="app-link-button"
              >
                <a-icon type="apple" />
                {{ $t("mfa.appStore") }}
              </a-button>
              <a-button
                type="link"
                size="large"
                @click="openAppStore('android')"
                class="app-link-button"
              >
                <a-icon type="android" />
                {{ $t("mfa.googlePlay") }}
              </a-button>
            </div>
            <div class="app-names">
              <a-tag color="blue" class="app-name-tag"
                >Google Authenticator</a-tag
              >
              <a-tag color="blue" class="app-name-tag"
                >Microsoft Authenticator</a-tag
              >
              <a-tag color="blue" class="app-name-tag">Authy</a-tag>
            </div>
          </div>
        </a-card>

        <!-- QR Code Section -->
        <a-card class="qr-card" :bordered="false">
          <div slot="title" class="instruction-title">
            <a-icon type="qrcode" />
            <span>{{ $t("mfa.step2Scan") }}</span>
          </div>
          <div class="qr-content">
            <div class="qr-wrapper">
              <div v-if="qrCode" class="qr-code-container">
                <img :src="qrCode" alt="QR Code" class="qr-code-image" />
                <div class="qr-overlay">
                  <a-icon type="scan" class="scan-icon" />
                </div>
              </div>
              <a-spin v-else size="large" class="qr-loading" />
            </div>
            <p class="qr-instruction">{{ $t("mfa.scanQRCodeInstruction") }}</p>
          </div>
        </a-card>

        <!-- Manual Entry -->
        <a-card class="manual-entry-card" :bordered="false">
          <div slot="title" class="instruction-title">
            <a-icon type="keyboard" />
            <span>{{ $t("mfa.cantScan") }}</span>
          </div>
          <div class="manual-entry-content">
            <p class="manual-entry-text">
              {{ $t("mfa.manualEntryDescription") }}
            </p>
            <a-input :value="secret" readonly class="secret-input" size="large">
              <a-button
                slot="suffix"
                type="link"
                @click="copySecret"
                :title="$t('mfa.copySecret')"
              >
                <a-icon type="copy" />
              </a-button>
            </a-input>
          </div>
        </a-card>

        <div class="action-buttons">
          <a-button @click="currentStep = 0" size="large" class="back-button">
            <a-icon type="arrow-left" />
            {{ $t("action.retour") }}
          </a-button>
          <a-button
            type="primary"
            size="large"
            @click="currentStep = 2"
            class="next-button"
          >
            {{ $t("mfa.iHaveScanned") }}
            <a-icon type="arrow-right" />
          </a-button>
        </div>
      </div>

      <!-- Step 2: Setup (SMS method) -->
      <div
        v-if="currentStep === 1 && selectedMethod === 'sms'"
        class="sms-setup"
      >
        <div class="setup-header">
          <h3 class="section-title">{{ $t("mfa.setupSMS") }}</h3>
          <p class="section-description">{{ $t("mfa.setupSMSDescription") }}</p>
        </div>

        <a-card class="sms-info-card" :bordered="false">
          <a-icon type="phone" class="sms-icon" />
          <h4 class="sms-title">{{ $t("mfa.smsReady") }}</h4>
          <p class="sms-description">{{ $t("mfa.smsReadyDescription") }}</p>
          <div class="phone-display">
            <a-icon type="mobile" />
            <span>{{ userPhone }}</span>
          </div>
        </a-card>

        <div class="action-buttons">
          <a-button @click="currentStep = 0" size="large" class="back-button">
            <a-icon type="arrow-left" />
            {{ $t("action.retour") }}
          </a-button>
          <a-button
            type="primary"
            size="large"
            @click="handleEnableSMS"
            :loading="loading"
            class="enable-button"
          >
            {{ $t("mfa.enable") }}
            <a-icon type="check" />
          </a-button>
        </div>
      </div>

      <!-- Step 3: Verify Code -->
      <div v-if="currentStep === 2" class="verify-step">
        <div class="setup-header">
          <h3 class="section-title">{{ $t("mfa.verificationRequired") }}</h3>
          <p class="section-description">
            {{
              selectedMethod === "app"
                ? $t("mfa.enterAppCodeDescription")
                : $t("mfa.enterSMSCodeDescription")
            }}
          </p>
        </div>

        <a-card class="verify-card" :bordered="false">
          <a-form :form="verifyForm" @submit="handleVerify" class="verify-form">
            <a-form-item
              :label="$t('mfa.verificationCode')"
              class="code-input-item"
            >
              <a-input
                v-decorator="[
                  'code',
                  {
                    rules: [
                      {
                        required: true,
                        message: $t('mfa.codeRequired'),
                      },
                      {
                        len: 6,
                        message: $t('mfa.codeLength'),
                      },
                    ],
                  },
                ]"
                :placeholder="$t('mfa.enterCode')"
                :maxLength="6"
                size="large"
                class="code-input"
                autocomplete="off"
                autofocus
              >
                <template slot="prefix">
                  <a-icon type="safety-certificate" />
                </template>
              </a-input>
              <div class="code-hint">
                <a-icon type="info-circle" />
                <span>{{ $t("mfa.codeHint") }}</span>
              </div>
            </a-form-item>
            <a-form-item class="verify-buttons">
              <a-button
                @click="currentStep = 1"
                size="large"
                class="back-button"
              >
                <a-icon type="arrow-left" />
                {{ $t("action.retour") }}
              </a-button>
              <a-button
                type="primary"
                html-type="submit"
                size="large"
                :loading="verifying"
                class="verify-button"
              >
                {{ $t("mfa.verify") }}
                <a-icon type="check" />
              </a-button>
            </a-form-item>
          </a-form>
        </a-card>
      </div>

      <!-- Step 4: Complete -->
      <div v-if="currentStep === 3" class="complete-step">
        <a-result
          status="success"
          :title="$t('mfa.enabled')"
          :sub-title="$t('mfa.enabledDescription')"
          class="success-result"
        >
          <template slot="extra">
            <div
              v-if="backupCodes && backupCodes.length > 0"
              class="backup-codes-section"
            >
              <a-card class="backup-codes-card" :bordered="false">
                <div slot="title" class="backup-codes-title">
                  <a-icon type="warning" theme="filled" />
                  <span>{{ $t("mfa.backupCodes") }}</span>
                </div>
                <div class="backup-codes-content">
                  <p class="backup-codes-warning">
                    {{ $t("mfa.backupCodesWarning") }}
                  </p>
                  <p class="backup-codes-description">
                    {{ $t("mfa.backupCodesDescription") }}
                  </p>
                  <div class="backup-codes-grid">
                    <a-tag
                      v-for="(code, index) in backupCodes"
                      :key="index"
                      color="orange"
                      class="backup-code-tag"
                    >
                      {{ code }}
                    </a-tag>
                  </div>
                  <a-button
                    type="primary"
                    @click="copyBackupCodes"
                    class="copy-codes-button"
                    size="large"
                  >
                    <a-icon type="copy" />
                    {{ $t("mfa.copyCodes") }}
                  </a-button>
                </div>
              </a-card>
            </div>
            <a-button
              type="primary"
              size="large"
              @click="handleComplete"
              class="complete-button"
            >
              {{ $t("action.fermer") }}
            </a-button>
          </template>
        </a-result>
      </div>
    </div>
  </div>
</template>

<script>
import * as jwt from "@/services/jwt";
import Vue from "vue";

export default {
  name: "MFASetup",
  props: {
    userPhone: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      currentStep: 0,
      stepStatus: "process",
      selectedMethod: "app",
      secret: null,
      qrCode: null,
      qrUrl: null,
      loading: false,
      verifying: false,
      verifyForm: this.$form.createForm(this),
      backupCodes: [],
      hasPhone: !!this.userPhone,
    };
  },
  methods: {
    async handleMethodChange() {
      // Reset when method changes
      this.secret = null;
      this.qrCode = null;
      this.qrUrl = null;
    },
    async handleSetup() {
      if (!this.selectedMethod) return;

      this.loading = true;
      try {
        const result = await jwt.setupMFA(this.selectedMethod);
        if (this.selectedMethod === "app") {
          this.secret = result.secret;
          this.qrUrl = result.qrUrl;
          this.qrCode = result.qrCode;
          this.currentStep = 1;
        } else if (this.selectedMethod === "sms") {
          this.currentStep = 1;
        }
      } catch (error) {
        // Error notification handled in service
      } finally {
        this.loading = false;
      }
    },
    async handleEnableSMS() {
      this.loading = true;
      try {
        await jwt.enableMFA("sms");
        this.$notification.success({
          message: this.$t("mfa.enabled"),
          description: this.$t("mfa.smsEnabledDescription"),
        });
        this.$emit("enabled", { method: "sms" });
      } catch (error) {
        // Error notification handled in service
      } finally {
        this.loading = false;
      }
    },
    handleVerify(e) {
      e.preventDefault();
      this.verifyForm.validateFields(async (err, values) => {
        if (!err) {
          this.verifying = true;
          try {
            const result = await jwt.enableMFA(
              this.selectedMethod,
              values.code
            );
            if (result.backupCodes) {
              this.backupCodes = result.backupCodes;
            }
            this.currentStep = 3;
            this.stepStatus = "finish";
            // Emit enabled event immediately after successful verification
            this.$emit("enabled", {
              method: this.selectedMethod,
              backupCodes: this.backupCodes,
            });
          } catch (error) {
            // Error notification handled in service
          } finally {
            this.verifying = false;
          }
        }
      });
    },
    copyBackupCodes() {
      const codesText = this.backupCodes.join("\n");
      const textarea = document.createElement("textarea");
      textarea.value = codesText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      Vue.prototype.$notification.success({
        message: this.$t("mfa.codesCopied"),
      });
    },
    copySecret() {
      if (this.secret) {
        const textarea = document.createElement("textarea");
        textarea.value = this.secret;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        Vue.prototype.$notification.success({
          message: this.$t("mfa.secretCopied"),
        });
      }
    },
    openAppStore(platform) {
      if (platform === "ios") {
        window.open(
          "https://apps.apple.com/app/google-authenticator/id388497605",
          "_blank"
        );
      } else {
        window.open(
          "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
          "_blank"
        );
      }
    },
    handleComplete() {
      this.$emit("enabled", {
        method: this.selectedMethod,
        backupCodes: this.backupCodes,
      });
    },
  },
};
</script>

<style scoped>
.mfa-setup-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
}

.mfa-steps {
  margin-bottom: 32px;
}

.mfa-content {
  min-height: 400px;
}

/* Method Selection */
.method-selection {
  padding: 8px 0;
}

.intro-section {
  text-align: center;
  margin-bottom: 32px;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
}

.section-description {
  font-size: 16px;
  color: #595959;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

.method-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.method-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  position: relative;
}

.method-card:hover {
  border-color: #1890ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
}

.method-card.selected {
  border-color: #1890ff;
  background-color: #f0f7ff;
}

.method-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.recommended-card {
  border-color: #52c41a;
}

.recommended-card.selected {
  border-color: #52c41a;
  background-color: #f6ffed;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.method-icon-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

.method-icon {
  font-size: 32px;
  color: #1890ff;
}

.recommended-badge {
  position: absolute;
  top: -8px;
  right: -8px;
}

.card-body {
  padding: 0;
}

.method-title {
  font-size: 20px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 8px;
}

.method-description {
  font-size: 15px;
  color: #595959;
  line-height: 1.6;
  margin-bottom: 16px;
}

.method-benefits {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #262626;
}

.benefit-icon {
  color: #52c41a;
  font-size: 16px;
}

.phone-warning {
  margin-top: 16px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
}

.continue-button,
.next-button,
.enable-button,
.verify-button,
.complete-button {
  min-width: 150px;
  height: 44px;
  font-size: 16px;
}

.back-button {
  height: 44px;
  font-size: 16px;
}

/* App Setup */
.app-setup {
  padding: 8px 0;
}

.setup-header {
  text-align: center;
  margin-bottom: 32px;
}

.instruction-card,
.qr-card,
.manual-entry-card {
  margin-bottom: 24px;
  border-radius: 8px;
}

.instruction-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #262626;
}

.instruction-content {
  padding: 8px 0;
}

.instruction-text {
  font-size: 16px;
  color: #595959;
  line-height: 1.6;
  margin-bottom: 16px;
}

.app-links {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
}

.app-link-button {
  font-size: 16px;
  height: 40px;
  padding: 0 20px;
}

.app-names {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.app-name-tag {
  font-size: 14px;
  padding: 4px 12px;
  height: auto;
}

.qr-content {
  text-align: center;
  padding: 8px 0;
}

.qr-wrapper {
  margin-bottom: 16px;
}

.qr-code-container {
  position: relative;
  display: inline-block;
  padding: 20px;
  background: white;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.qr-code-image {
  max-width: 280px;
  width: 100%;
  height: auto;
  display: block;
}

.qr-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.scan-icon {
  font-size: 48px;
  color: rgba(24, 144, 255, 0.3);
}

.qr-loading {
  padding: 40px;
}

.qr-instruction {
  font-size: 16px;
  color: #595959;
  line-height: 1.6;
  max-width: 80%;
  margin: 0 auto;
}

.manual-entry-content {
  padding: 8px 0;
}

.manual-entry-text {
  font-size: 15px;
  color: #595959;
  margin-bottom: 12px;
}

.secret-input {
  font-family: "Courier New", monospace;
  font-size: 16px;
  letter-spacing: 2px;
}

/* SMS Setup */
.sms-setup {
  padding: 8px 0;
}

.sms-info-card {
  text-align: center;
  margin-bottom: 32px;
  border-radius: 8px;
}

.sms-icon {
  font-size: 64px;
  color: #1890ff;
  margin-bottom: 16px;
}

.sms-title {
  font-size: 22px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
}

.sms-description {
  font-size: 16px;
  color: #595959;
  line-height: 1.6;
  margin-bottom: 20px;
}

.phone-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f0f7ff;
  border-radius: 6px;
  font-size: 18px;
  font-weight: 500;
  color: #1890ff;
}

/* Verify Step */
.verify-step {
  padding: 8px 0;
}

.verify-card {
  max-width: 500px;
  margin: 0 auto;
  border-radius: 8px;
}

.verify-form {
  padding: 8px 0;
}

.code-input-item {
  margin-bottom: 24px;
}

.code-input {
  font-size: 24px;
  text-align: center;
  letter-spacing: 8px;
  font-family: "Courier New", monospace;
}

.code-input :deep(.ant-input-affix-wrapper) {
  height: 60px;
  display: flex;
  align-items: center;
}

.code-input :deep(.ant-input) {
  height: 60px;
  line-height: 60px;
}

.code-input :deep(.ant-input-prefix) {
  display: inline-flex;
  align-items: center;
  height: 100%;
  left: 12px;
  position: absolute;
}

.code-input :deep(.ant-input-prefix .anticon) {
  font-size: 18px;
  color: #8c8c8c;
  line-height: 1;
  vertical-align: middle;
}

.code-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 14px;
  color: #8c8c8c;
}

.verify-buttons {
  margin-top: 32px;
  display: flex;
  justify-content: center;
  gap: 12px;
}

/* Complete Step */
.complete-step {
  padding: 8px 0;
}

.success-result {
  padding: 24px 0;
}

.backup-codes-section {
  max-width: 600px;
  margin: 0 auto 24px;
}

.backup-codes-card {
  border-radius: 8px;
  border: 2px solid #faad14;
}

.backup-codes-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #fa8c16;
}

.backup-codes-content {
  padding: 8px 0;
}

.backup-codes-warning {
  font-size: 16px;
  font-weight: 600;
  color: #fa8c16;
  margin-bottom: 8px;
}

.backup-codes-description {
  font-size: 15px;
  color: #595959;
  line-height: 1.6;
  margin-bottom: 20px;
}

.backup-codes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.backup-code-tag {
  font-family: "Courier New", monospace;
  font-size: 16px;
  padding: 8px 12px;
  text-align: center;
  font-weight: 600;
}

.copy-codes-button {
  width: 100%;
  margin-top: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .mfa-setup-container {
    padding: 16px;
  }

  .method-cards {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 20px;
  }

  .section-description {
    font-size: 14px;
  }

  .qr-code-image {
    max-width: 240px;
  }

  .code-input {
    font-size: 20px;
    letter-spacing: 4px;
  }

  .backup-codes-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
